import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { RoomCategory } from '@gameplay/management/rooms/RoomTypes';

export interface RoomColorScheme {
  bedroom: number;
  bathroom: number;
  dining: number;
  medical: number;
  recreation: number;
  utility: number;
  unspecified: number;
}

export class RoomLegend extends Container {
  private background!: Graphics;
  private legendItems: Container[] = [];
  private isVisible: boolean = false;

  // Room type color scheme
  public static readonly ROOM_COLORS: RoomColorScheme = {
    'bedroom': 0x4A90E2,      // Blue
    'bathroom': 0x7ED321,     // Green  
    'dining': 0xF5A623,       // Orange
    'medical': 0xD0021B,      // Red
    'recreation': 0xB8E986,   // Light Green
    'utility': 0x50E3C2,      // Teal
    'unspecified': 0x666666   // Gray
  };

  constructor() {
    super();
    this.createLegend();
    this.visible = false;
  }

  private createLegend(): void {
    // Create background panel
    this.background = new Graphics();
    this.background.beginFill(0x000000, 0.8);
    this.background.lineStyle(2, 0xffffff, 1);
    this.background.drawRoundedRect(0, 0, 180, 220, 8);
    this.background.endFill();
    this.addChild(this.background);

    // Create title
    const titleStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
      fontWeight: 'bold'
    });

    const title = new Text('Room Types', titleStyle);
    title.position.set(10, 10);
    this.addChild(title);

    // Create legend items
    const itemStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 12,
      fill: 0xffffff
    });

    let yOffset = 40;
    const roomTypes: Array<{ category: RoomCategory, label: string }> = [
      { category: 'bedroom', label: 'Bedroom' },
      { category: 'bathroom', label: 'Bathroom' },
      { category: 'dining', label: 'Dining' },
      { category: 'medical', label: 'Medical' },
      { category: 'recreation', label: 'Recreation' },
      { category: 'utility', label: 'Utility' }
    ];

    roomTypes.forEach(({ category, label }) => {
      const itemContainer = new Container();
      
      // Color swatch
      const swatch = new Graphics();
      swatch.beginFill(RoomLegend.ROOM_COLORS[category], 1);
      swatch.lineStyle(1, 0xffffff, 1);
      swatch.drawRect(0, 0, 16, 16);
      swatch.endFill();
      swatch.position.set(10, yOffset);
      itemContainer.addChild(swatch);
      
      // Label text
      const labelText = new Text(label, itemStyle);
      labelText.position.set(35, yOffset + 2);
      itemContainer.addChild(labelText);
      
      this.addChild(itemContainer);
      this.legendItems.push(itemContainer);
      
      yOffset += 25;
    });

    // Add unassigned room color
    const unassignedContainer = new Container();
    const unassignedSwatch = new Graphics();
    unassignedSwatch.beginFill(0x666666, 1);
    unassignedSwatch.lineStyle(1, 0xffffff, 1);
    unassignedSwatch.drawRect(0, 0, 16, 16);
    unassignedSwatch.endFill();
    unassignedSwatch.position.set(10, yOffset);
    unassignedContainer.addChild(unassignedSwatch);
    
    const unassignedText = new Text('Unassigned', itemStyle);
    unassignedText.position.set(35, yOffset + 2);
    unassignedContainer.addChild(unassignedText);
    
    this.addChild(unassignedContainer);
    this.legendItems.push(unassignedContainer);
  }

  public show(): void {
    this.isVisible = true;
    this.visible = true;
  }

  public hide(): void {
    this.isVisible = false;
    this.visible = false;
  }

  public toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  public positionAt(x: number, y: number): void {
    this.position.set(x, y);
  }

  public static getRoomColor(category: RoomCategory | null): number {
    if (!category) return 0x666666; // Gray for unassigned
    return RoomLegend.ROOM_COLORS[category];
  }
}